import { FilterX, FlaskConical, Paperclip, Plus, Zap } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useExamesControllerListar } from '@/api/generated/exames/exames';
import type { AnexoDoExameResponse } from '@/api/generated/model';
import { AnexoDoExameDialogo } from '@/features/exames/components/AnexoDoExameDialogo';
import { ExameExternoDialogo } from '@/features/exames/components/ExameExternoDialogo';
import { dataParaISO, formatarData, rotuloDeOrigem } from '@/features/exames/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { rotaDoExame } from '@/shared/lib/rotas';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const POR_PAGINA = 20;

/**
 * Exames do paciente, dentro do histórico.
 *
 * Continua sendo o recorte de UM paciente — nunca a fila da clínica, que é a
 * tela `/exames`. O que mudou é que o dossiê de um paciente antigo passa de
 * dezenas de linhas: sem busca e sem período, ler "o T4 do ano passado" virava
 * rolagem. Os filtros são do servidor (o mesmo `GET /exames` da outra tela),
 * não um `.filter()` sobre a primeira página — filtrar o que já foi baixado
 * mentiria sobre o que existe.
 *
 * A criação daqui é só a de exame **externo**: o exame que a clínica vai
 * executar nasce na fila, com catálogo e analitos; este nasce como documento,
 * e o lugar natural de registrá-lo é o prontuário do paciente, com ele na tela.
 */
export function AbaExames({ pacienteId }: { pacienteId: string }) {
  const { pode } = usePermissoes();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [formAberto, setFormAberto] = useState(false);
  const [exameDoAnexo, setExameDoAnexo] = useState<string | null>(null);
  const [anexoAberto, setAnexoAberto] = useState<AnexoDoExameResponse | null>(null);

  const { data, isPending, isError, refetch } = useExamesControllerListar({
    pacienteId,
    pagina,
    porPagina: POR_PAGINA,
    ordenarPor: 'criadoEm',
    ordem: 'desc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(de ? { dataDe: dataParaISO(de) } : {}),
    ...(ate ? { dataAte: dataParaISO(ate) } : {}),
  });

  const filtrando = Boolean(busca.trim() || de || ate);

  /** Todo filtro volta para a primeira página: a 3ª do resultado antigo não existe no novo. */
  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  function limpar() {
    setBusca('');
    setDe('');
    setAte('');
    setPagina(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Campo id="busca-exames" rotulo="Buscar" className="sm:max-w-xs flex-1 space-y-1.5">
            <Input
              id="busca-exames"
              value={busca}
              onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
              placeholder="Nome do exame, OS ou laboratório..."
              className={CLASSE_CONTROLE}
            />
          </Campo>

          <Campo id="exames-de" rotulo="De" className="space-y-1.5">
            <Input
              id="exames-de"
              type="date"
              value={de}
              max={ate || undefined}
              onChange={(e) => aoFiltrar(() => setDe(e.target.value))}
              className={CLASSE_CONTROLE}
            />
          </Campo>

          <Campo id="exames-ate" rotulo="Até" className="space-y-1.5">
            <Input
              id="exames-ate"
              type="date"
              value={ate}
              min={de || undefined}
              onChange={(e) => aoFiltrar(() => setAte(e.target.value))}
              className={CLASSE_CONTROLE}
            />
          </Campo>

          {filtrando && (
            <Button variant="ghost" onClick={limpar} className="self-end">
              <FilterX className="w-4 h-4" />
              Limpar
            </Button>
          )}
        </div>

        {pode('exames.criar') && (
          <Button onClick={() => setFormAberto(true)}>
            <Plus className="w-4 h-4" />
            Exame externo
          </Button>
        )}
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={FlaskConical}
            titulo={filtrando ? 'Nenhum exame no filtro' : 'Nenhum exame'}
            descricao={
              filtrando
                ? 'Tente outro termo ou amplie o período.'
                : 'Os exames deste paciente aparecem aqui assim que forem lançados. Um exame feito fora entra por "Exame externo".'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Exame</TableHead>
                    <TableHead>Origem</TableHead>
                    <TableHead>Coleta</TableHead>
                    <TableHead className="text-right">Resultados</TableHead>
                    <TableHead className="w-24 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((exame) => (
                    <TableRow key={exame.id}>
                      <TableCell>
                        <span className="font-medium text-foreground">{exame.exameNome}</span>
                        {exame.urgente && (
                          <Badge
                            variant="outline"
                            className="ml-2 text-destructive border-destructive/40"
                          >
                            <Zap className="w-3 h-3 mr-1" />
                            Urgente
                          </Badge>
                        )}
                        {exame.os && (
                          <p className="text-xs text-muted-foreground mt-0.5">OS {exame.os}</p>
                        )}
                        {exame.laboratorio && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {exame.laboratorio}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {rotuloDeOrigem(exame.origem)}
                      </TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatarData(exame.coletadoEm)}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* O exame externo não tem analito: o resultado dele é o
                            documento. "0/0" seria uma contagem sem sentido, e o
                            que a pessoa quer saber ali é se há laudo anexado. */}
                        {exame.totalAnalitos === 0 ? (
                          exame.anexos.length > 0 ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
                              {exame.anexos.length}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )
                        ) : exame.origem === 'EXTERNO' ? (
                          // No externo não há item de catálogo a completar:
                          // "3/3" leria como "nada falta", e o que existe é
                          // "o laudo trouxe 3 valores".
                          <span className="text-muted-foreground">
                            {exame.resultados.length}{' '}
                            {exame.resultados.length === 1 ? 'valor' : 'valores'}
                          </span>
                        ) : (
                          <span
                            className={
                              exame.resultados.length >= exame.totalAnalitos
                                ? 'font-medium text-emerald-600'
                                : 'text-muted-foreground'
                            }
                          >
                            {exame.resultados.length}/{exame.totalAnalitos}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Um anexo só abre direto — o caso comum é o laudo
                            único, e mandar o leitor à página do exame para
                            clicar de novo seria um passo a troco de nada. */}
                        {exame.anexos.length === 1 && exame.anexos[0] ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setExameDoAnexo(exame.id);
                              setAnexoAberto(exame.anexos[0] ?? null);
                            }}
                          >
                            Ver laudo
                          </Button>
                        ) : (
                          <Button asChild variant="ghost" size="sm">
                            <Link to={rotaDoExame(exame.id)}>Ver laudo</Link>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <ExameExternoDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        pacienteId={pacienteId}
      />

      {exameDoAnexo && (
        <AnexoDoExameDialogo
          exameId={exameDoAnexo}
          anexo={anexoAberto}
          aoFechar={() => setAnexoAberto(null)}
        />
      )}
    </div>
  );
}
